import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useParams } from "wouter";
import { useEffect, useRef, useState } from "react";
import { apiRequest, queryClient, getExtraHeaders } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  BookOpen,
  Loader2,
  ChevronUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DOMPurify from "dompurify";

interface ReadingMaterial {
  id: string;
  title: string;
  slug: string;
  grade: number;
  subject: string;
  topic: string;
  content: string;
  summary: string | null;
  coverImageUrl: string | null;
  estimatedReadTime: number;
  status: string;
}

interface ReadingProgress {
  id: string;
  materialId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  scrollPosition: number;
  totalTimeSeconds: number;
  completedAt: string | null;
}

interface MaterialWithProgress {
  material: ReadingMaterial;
  progress: ReadingProgress | null;
}

export default function ReadingView() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const sessionStartRef = useRef<number>(Date.now());
  const lastUpdateRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, authLoading, setLocation]);

  const { data, isLoading, error } = useQuery<MaterialWithProgress>({
    queryKey: ['/api/reading', slug],
    enabled: isAuthenticated && !!slug,
  });

  const startSessionMutation = useMutation({
    mutationFn: async (materialId: string) => {
      return apiRequest('POST', `/api/reading/${materialId}/start`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reading', slug] });
    }
  });

  const updateProgressMutation = useMutation({
    mutationFn: async ({ materialId, scrollPosition, timeSpent }: { 
      materialId: string; 
      scrollPosition: number;
      timeSpent: number;
    }) => {
      return apiRequest('POST', `/api/reading/${materialId}/progress`, {
        scrollPosition,
        timeSpent
      });
    }
  });

  const completeMutation = useMutation({
    mutationFn: async (materialId: string) => {
      return apiRequest('POST', `/api/reading/${materialId}/complete`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reading', slug] });
      queryClient.invalidateQueries({ queryKey: ['/api/reading'] });
      toast({
        title: "Reading Complete!",
        description: "Great job completing this lesson!"
      });
    }
  });

  const endSessionMutation = useMutation({
    mutationFn: async (materialId: string) => {
      const timeSpent = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      return apiRequest('POST', `/api/reading/${materialId}/end-session`, { timeSpent });
    }
  });

  useEffect(() => {
    if (data?.material && !data?.progress) {
      startSessionMutation.mutate(data.material.id);
    }
  }, [data?.material?.id, data?.progress]);

  useEffect(() => {
    const timer = setInterval(() => {
      setReadingTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const scrollTop = window.scrollY - element.offsetTop;
      const scrollHeight = element.scrollHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(100, (scrollTop / scrollHeight) * 100));
      
      setScrollProgress(progress);
      setShowScrollTop(window.scrollY > 300);

      if (data?.material && Date.now() - lastUpdateRef.current > 10000) {
        const timeSpent = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        updateProgressMutation.mutate({
          materialId: data.material.id,
          scrollPosition: progress,
          timeSpent
        });
        lastUpdateRef.current = Date.now();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data?.material]);

  // "Your turn to write" boxes inside the content: save as the student types and offer
  // teacher-style feedback. The content is sanitised HTML, so the controls are attached here.
  useEffect(() => {
    const root = contentRef.current; const materialId = data?.material?.id;
    if (!root || !materialId) return;
    const areas = Array.from(root.querySelectorAll<HTMLTextAreaElement>('textarea[data-prompt]'));
    if (areas.length === 0) return;
    const headers = { 'Content-Type': 'application/json', ...getExtraHeaders() };
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const cleanups: Array<() => void> = [];
    const esc = (t: string) => t.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
    const renderFeedback = (area: HTMLTextAreaElement, fb: any) => {
      let box = area.parentElement?.querySelector<HTMLDivElement>('.efa-feedback');
      if (!box) { box = document.createElement('div'); box.className = 'efa-feedback'; area.parentElement?.appendChild(box); }
      const li = (xs: string[]) => xs.map(x => `<li>${esc(x)}</li>`).join('');
      box.innerHTML = `<h4>Teacher feedback<span class="efa-score">${Number(fb.score) || 0} / ${Number(fb.outOf) || 10}</span></h4>` +
        `<p>${esc(fb.summary || '')}</p>` +
        (fb.strengths?.length ? `<p><b>What you did well</b></p><ul>${li(fb.strengths)}</ul>` : '') +
        (fb.improvements?.length ? `<p><b>Make it even better</b></p><ul class="efa-improve">${li(fb.improvements)}</ul>` : '') +
        (fb.nextStep ? `<p><b>Next step:</b> ${esc(fb.nextStep)}</p>` : '');
    };
    for (const area of areas) {
      const key = area.dataset.prompt!;
      let actions = area.parentElement?.querySelector<HTMLDivElement>('.efa-write-actions');
      if (!actions) {
        actions = document.createElement('div'); actions.className = 'efa-write-actions';
        actions.innerHTML = '<button type="button" class="efa-btn" data-action="feedback">Get feedback</button><span class="efa-save-status"></span>';
        area.insertAdjacentElement('afterend', actions);
      }
      const status = actions.querySelector<HTMLSpanElement>('.efa-save-status')!;
      const button = actions.querySelector<HTMLButtonElement>('button')!;
      const save = async () => {
        status.textContent = 'Saving...';
        try {
          const r = await fetch(`/api/reading/${materialId}/responses/${key}`, { method: 'PUT', credentials: 'include', headers, body: JSON.stringify({ text: area.value }) });
          status.textContent = r.ok ? 'Saved' : 'Could not save';
        } catch { status.textContent = 'Offline, will retry'; }
      };
      const onInput = () => { status.textContent = ''; clearTimeout(timers.get(key)); timers.set(key, setTimeout(save, 900)); };
      const onFeedback = async () => {
        clearTimeout(timers.get(key)); await save();
        button.disabled = true; status.textContent = 'Reading your work...';
        try {
          const promptText = area.closest('.efa-write')?.querySelector('p')?.textContent || '';
          const r = await fetch(`/api/reading/${materialId}/responses/${key}/feedback`, { method: 'POST', credentials: 'include', headers, body: JSON.stringify({ promptText }) });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.feedback) { renderFeedback(area, j.feedback); status.textContent = 'Saved'; }
          else status.textContent = j.error || 'Feedback is not available right now';
        } catch { status.textContent = 'Could not reach the server'; }
        button.disabled = false;
      };
      area.addEventListener('input', onInput); button.addEventListener('click', onFeedback);
      cleanups.push(() => { area.removeEventListener('input', onInput); button.removeEventListener('click', onFeedback); clearTimeout(timers.get(key)); });
    }
    fetch(`/api/reading/${materialId}/responses`, { credentials: 'include', headers: getExtraHeaders() })
      .then(r => (r.ok ? r.json() : { responses: [] }))
      .then(j => { for (const r of j.responses || []) { const a = areas.find(x => x.dataset.prompt === r.promptKey); if (!a) continue; if (!a.value) a.value = r.text || ''; if (r.feedback) renderFeedback(a, r.feedback); } })
      .catch(() => {});
    return () => cleanups.forEach(fn => fn());
  }, [data?.material?.id, data?.material?.content]);

  useEffect(() => {
    return () => {
      if (data?.material) {
        endSessionMutation.mutate(data.material.id);
      }
    };
  }, [data?.material?.id]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMarkComplete = () => {
    if (data?.material) {
      completeMutation.mutate(data.material.id);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Reading material not found</h3>
            <p className="text-muted-foreground mb-4">
              This reading material may have been removed or is not available.
            </p>
            <Button onClick={() => setLocation('/reading')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { material, progress } = data;
  const isCompleted = progress?.status === 'completed';
  const sanitizedContent = DOMPurify.sanitize(material.content);

  return (
    <div className="min-h-screen">
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-3 max-w-4xl">
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" size="sm" onClick={() => setLocation('/reading')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{formatTime(readingTime)}</span>
              </div>
              
              {isCompleted ? (
                <Badge className="bg-green-500">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completed
                </Badge>
              ) : (
                <Button 
                  size="sm" 
                  onClick={handleMarkComplete}
                  disabled={completeMutation.isPending}
                >
                  {completeMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Mark Complete
                </Button>
              )}
            </div>
          </div>
          
          <Progress value={scrollProgress} className="h-1 mt-2" />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl pt-24" ref={contentRef}>
        {material.coverImageUrl && (
          <div className="aspect-video w-full overflow-hidden rounded-lg mb-8">
            <img 
              src={material.coverImageUrl} 
              alt={material.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="outline" className="capitalize">{material.subject}</Badge>
            <Badge variant="outline" className="capitalize">{material.topic}</Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{material.estimatedReadTime} min read</span>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{material.title}</h1>
          
          {material.summary && (
            <p className="text-lg text-muted-foreground">
              {material.summary}
            </p>
          )}
        </div>

        <Card>
          <CardContent className="prose prose-lg dark:prose-invert max-w-none py-8">
            <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
          </CardContent>
        </Card>

        <div className="mt-8 flex flex-col items-center gap-4">
          {!isCompleted && (
            <Button 
              size="lg"
              onClick={handleMarkComplete}
              disabled={completeMutation.isPending}
            >
              {completeMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Mark as Complete
            </Button>
          )}
          
          <Button variant="outline" onClick={() => setLocation('/reading')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Library
          </Button>
        </div>
      </div>

      {showScrollTop && (
        <Button
          className="fixed bottom-6 right-6 rounded-full"
          size="icon"
          onClick={scrollToTop}
        >
          <ChevronUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}
